import { PropsWithChildren, useState } from 'react';

import { SearchOutlined, SwapOutlined, CheckOutlined } from '@ant-design/icons';
import { Button, Divider, Dropdown, Empty, Input, Menu, Modal, Typography, Avatar } from 'antd';
import { useNavigate } from 'react-router-dom';
import shallow from 'zustand/shallow';

import { ROUTES } from '@/config/routes';
import { PRISMA_COMPANY_ID } from '@/constants/enums';
import { useIrmCalendarStore } from '@/features/irm/stores/useIrmCalendarStore';
import { useMyCompanies } from '@/features/login/api/getMyCompanies';
import { hasHomeAccess } from '@/features/login/utils/hasHomeAccess';
import { useDisclosure } from '@/hooks/useDisclosure';
import { useViewStore } from '@/stores/useViewStore';
import { parseRadioOpts } from '@/utils/parseRadioOpts';

import styles from './style.module.less';

const { Text } = Typography;

export const ChangeCompanyDropdown = ({ children }: PropsWithChildren<unknown>) => {
  const navigate = useNavigate();
  const { open, close, isOpen } = useDisclosure();
  const myCompaniesQuery = useMyCompanies({});

  const { view, company, setCompany } = useViewStore(
    (state) => ({ view: state.view, company: state.company, setCompany: state.setCompany }),
    shallow,
  );

  const [search, setSearch] = useState(``);
  const [selectedKey, setSelectedKey] = useState<string | undefined>(company);

  const changeViewLabel = view === `prisma` ? `empresa` : `usuário`;

  const filteredItems =
    myCompaniesQuery.data?.data.data.filter((item) =>
      item.name.toLowerCase().includes(search.toLowerCase()),
    ) ?? [];

  const currentCompany = myCompaniesQuery.data?.data.data?.find((item) => item.id === company);

  const handleChangeCompany: React.ComponentProps<typeof Menu>['onClick'] = (e) => {
    if (e.key === selectedKey) return;
    setSelectedKey(e.key);
    open();
  };

  const { setDates } = useIrmCalendarStore(
    (state) => ({
      initial_date: state.initial_date,
      final_date: state.final_date,
      setDates: state.setDates,
    }),
    shallow,
  );

  const handleConfirm = () => {
    if (selectedKey === undefined) return;
    const selectedCompany = myCompaniesQuery.data?.data.data.find((item) => item.id === selectedKey);
    const nextRoute =
      selectedKey !== PRISMA_COMPANY_ID && !hasHomeAccess(selectedCompany)
        ? ROUTES.getLink('services')
        : `/`;
    setCompany(selectedKey);
    if (selectedKey !== PRISMA_COMPANY_ID) {
      setDates({
        initial_date: null,
        final_date: null,
      });
    }
    close();
    navigate(nextRoute);
  };

  const handleCancel = () => {
    if (company) {
      setSelectedKey(company);
    }
    close();
  };

  const enhancedMenuItems = parseRadioOpts(filteredItems, `id`, `name`).map((item) => {
    const companyData = filteredItems.find((companyItem) => companyItem.id === item.key);
    const isSelected = item.key === company;

    return {
      ...item,
      label: (
        <div className={styles['enhanced-menu-item']}>
          <div className={styles['company-info']}>
            <Avatar src={companyData?.more?.logoURL} size={24} className={styles['company-logo']}>
              {companyData?.name.charAt(0).toUpperCase()}
            </Avatar>
            <span className={styles['company-name-text']}>{item.label}</span>
          </div>
          {isSelected && <CheckOutlined className={styles['selected-icon']} />}
        </div>
      ),
    };
  });

  return myCompaniesQuery.data?.data?.data && myCompaniesQuery.data?.data?.data.length > 1 ? (
    <>
      <Dropdown
        placement="bottomRight"
        trigger={['click']}
        getPopupContainer={(trigger) => trigger.parentElement ?? document.body}
        destroyPopupOnHide
        overlay={
          <div className={styles[`change-company-dropdown`]}>
            {/* Header do dropdown */}
            <div className={styles['dropdown-header']}>
              <div className={styles['header-content']}>
                <SwapOutlined className={styles['header-icon']} />
                <div className={styles['header-text']}>
                  <Text className={styles['header-title']}>Mudar Perfil</Text>
                  <Text className={styles['header-subtitle']}>
                    Atual: {currentCompany?.name || 'Nenhuma selecionada'}
                  </Text>
                </div>
              </div>
            </div>

            <div className={styles['dropdown-content']}>
              <Input
                allowClear
                placeholder="Pesquisar empresas..."
                prefix={<SearchOutlined />}
                onChange={(e) => setSearch(e.target.value)}
                value={search}
                className={styles[`change-company-dropdown__input`]}
              />

              <Divider className={styles[`change-company-dropdown__divider`]} />

              {filteredItems.length > 0 ? (
                <Menu
                  className={styles[`change-company-dropdown__menu`]}
                  items={enhancedMenuItems}
                  onClick={handleChangeCompany}
                  selectedKeys={selectedKey ? [selectedKey] : undefined}
                  style={{ boxShadow: `none` }}
                />
              ) : (
                <div className={styles['empty-container']}>
                  <Empty
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description="Nenhuma empresa encontrada"
                    className={styles['empty-state']}
                  />
                </div>
              )}
            </div>
          </div>
        }
      >
        {children}
      </Dropdown>

      <Modal
        title="Confirmar troca de empresa"
        open={isOpen}
        onCancel={close}
        afterClose={handleCancel}
        footer={[
          <Button key="cancel" onClick={handleCancel}>
            Cancelar
          </Button>,
          <Button key="ok" type="primary" onClick={handleConfirm}>
            Confirmar
          </Button>,
        ]}
      >
        <p>
          Tem certeza que deseja trocar para visão de <strong>{changeViewLabel}</strong>?
        </p>
        <p>
          <Text type="secondary">
            Alguns itens não estarão disponíveis quando se muda para visão de {changeViewLabel}.
          </Text>
        </p>
      </Modal>
    </>
  ) : null;
};
